/*
 * Copyright (c) CIRROM 2025.
 */

/**
 * Recovery for webpack chunk-load failures.
 *
 * After a deploy, a browser holding a cached index.html asks for a
 * contenthashed chunk that no longer exists. IIS (public/web.config) rewrites
 * any non-existent file to "/", so the request answers 200 with index.html
 * instead of 404: the JS chunk is refused (nosniff) and the CSS chunk fails
 * strict MIME checking. Both surface as a rejected import() promise.
 *
 * React.lazy caches a rejected payload permanently, so the failing route can
 * never recover in place - only a fresh page load can.
 */

const CHUNK_RELOAD_KEY = "cirrom-chunk-reload";

/**
 * How long a recorded reload attempt stays "spent". Long enough that the
 * repeat failure proving the reload did not help (seconds later) still sees
 * it, short enough that a long-lived tab is not poisoned for the next deploy.
 */
const RELOAD_GUARD_TTL_MS = 5 * 60 * 1000;

/**
 * Message fallbacks, used only when neither name nor code identifies the
 * failure. The last two cover native-ESM bundlers so a future move off CRA
 * does not silently disable recovery. webpack's HMR failure ("Loading hot
 * update chunk ...") deliberately does not match - dev-only, must never
 * trigger a reload.
 */
const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /Loading chunk\s+\S+\s+failed/i,
  /Loading CSS chunk\s+\S+\s+failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
];

interface ChunkLoadErrorLike {
  name?: unknown;
  code?: unknown;
  message?: unknown;
}

/**
 * In-memory latch, so two boundaries catching the same failure in one tick
 * cannot both call reload().
 */
let reloadInFlight = false;

/** True for a bundler chunk-load failure, false for a genuine app error. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  let message = "";

  if (typeof error === "string") {
    message = error;
  } else if (typeof error === "object") {
    const candidate = error as ChunkLoadErrorLike;

    // webpack jsonp runtime sets name explicitly.
    if (candidate.name === "ChunkLoadError") {
      return true;
    }
    // mini-css-extract-plugin keeps name "Error", but code is stable.
    if (candidate.code === "CSS_CHUNK_LOAD_FAILED") {
      return true;
    }
    if (typeof candidate.message === "string") {
      message = candidate.message;
    }
  }

  return message ? CHUNK_ERROR_PATTERNS.some((p) => p.test(message)) : false;
}

function hasSpentReloadGuard(): boolean {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (raw === null) {
      return false;
    }
    const age = Date.now() - Number(raw);
    if (!Number.isFinite(age) || age < 0 || age > RELOAD_GUARD_TTL_MS) {
      // Expired, unparseable, or the system clock moved: allow a reload.
      window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return false;
    }
    return true;
  } catch {
    // Storage disabled (private mode, blocked cookies).
    return false;
  }
}

/**
 * Records the attempt and verifies by read-back that it persisted. If it did
 * not, the caller must not reload - an unrecorded attempt reloads forever.
 */
function markReloadAttempt(): boolean {
  const attemptedAt = String(Date.now());
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, attemptedAt);
    return window.sessionStorage.getItem(CHUNK_RELOAD_KEY) === attemptedAt;
  } catch {
    return false;
  }
}

/**
 * Reloads the page at most once per tab. Returns false when the single
 * automatic attempt has already been spent, storage cannot record it, or the
 * reload demonstrably did not help - the caller must then let the error
 * through rather than loop.
 *
 * window.location.reload() is used rather than a cache-busting query param:
 * it revalidates the top-level document, preserves the current URL, and does
 * not pollute window.location.search (App.tsx reads it for
 * originalAccessPath).
 */
export function recoverFromChunkLoadError(): boolean {
  if (reloadInFlight) {
    return true;
  }
  if (hasSpentReloadGuard() || !markReloadAttempt()) {
    return false;
  }

  reloadInFlight = true;
  window.location.reload();
  return true;
}
